import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaChartLine, FaUsers, FaHandshake, FaTrophy, FaShieldAlt, FaAward, FaBullseye, FaMobileAlt, FaHeadset, FaUserShield, FaCheckCircle } from 'react-icons/fa';
import { MdTrendingUp, MdAccountBalance, MdSecurity, MdSupport, MdVerifiedUser, MdCardGiftcard, MdSchool } from 'react-icons/md';
import Stats from '../components/home/Stats';

const FinancialInfo = () => {
    const { t } = useTranslation();

    const milestones = [
        { year: t('financialGrowth.milestones.year1'), title: t('financialGrowth.milestones.title1'), desc: t('financialGrowth.milestones.desc1') },
        { year: t('financialGrowth.milestones.year2'), title: t('financialGrowth.milestones.title2'), desc: t('financialGrowth.milestones.desc2') },
        { year: t('financialGrowth.milestones.year3'), title: t('financialGrowth.milestones.title3'), desc: t('financialGrowth.milestones.desc3') },
        { year: t('financialGrowth.milestones.year4'), title: t('financialGrowth.milestones.title4'), desc: t('financialGrowth.milestones.desc4') }
    ];

    const achievements = [
        t('financialGrowth.achievements.point1'),
        t('financialGrowth.achievements.point2'),
        t('financialGrowth.achievements.point3'),
        t('financialGrowth.achievements.point4'),
        t('financialGrowth.achievements.point5'),
        t('financialGrowth.achievements.point6')
    ];

    // Data from MemberServices.jsx
    const services = [
        {
            id: 1,
            icon: <MdAccountBalance />,
            titleKey: 'memberServices.services.financial.title',
            descKey: 'memberServices.services.financial.desc',
            features: [
                t('memberServices.services.financial.feature1'),
                t('memberServices.services.financial.feature2'),
                t('memberServices.services.financial.feature3'),
                t('memberServices.services.financial.feature4')
            ]
        },
        {
            id: 2,
            icon: <FaMobileAlt />,
            titleKey: 'memberServices.services.digital.title',
            descKey: 'memberServices.services.digital.desc',
            features: [
                t('memberServices.services.digital.feature1'),
                t('memberServices.services.digital.feature2'),
                t('memberServices.services.digital.feature3'),
                t('memberServices.services.digital.feature4')
            ]
        },
        {
            id: 3,
            icon: <MdSchool />,
            titleKey: 'memberServices.services.advisory.title',
            descKey: 'memberServices.services.advisory.desc',
            features: [
                t('memberServices.services.advisory.feature1'),
                t('memberServices.services.advisory.feature2'),
                t('memberServices.services.advisory.feature3'),
                t('memberServices.services.advisory.feature4')
            ]
        },
        {
            id: 4,
            icon: <MdSecurity />,
            titleKey: 'memberServices.services.insurance.title',
            descKey: 'memberServices.services.insurance.desc',
            features: [
                t('memberServices.services.insurance.feature1'),
                t('memberServices.services.insurance.feature2'),
                t('memberServices.services.insurance.feature3'),
                t('memberServices.services.insurance.feature4')
            ]
        },
        {
            id: 5,
            icon: <FaHeadset />,
            titleKey: 'memberServices.services.support.title',
            descKey: 'memberServices.services.support.desc',
            features: [
                t('memberServices.services.support.feature1'),
                t('memberServices.services.support.feature2'),
                t('memberServices.services.support.feature3'),
                t('memberServices.services.support.feature4')
            ]
        },
        {
            id: 6,
            icon: <MdCardGiftcard />,
            titleKey: 'memberServices.services.rewards.title',
            descKey: 'memberServices.services.rewards.desc',
            features: [
                t('memberServices.services.rewards.feature1'),
                t('memberServices.services.rewards.feature2'),
                t('memberServices.services.rewards.feature3'),
                t('memberServices.services.rewards.feature4')
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
            {/* 1. Hero Section - Corporate Style */}
            <section className="relative py-24 bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-4 block">{t('navbar.financialGrowthBadge')}</span>
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 font-display">{t('navbar.financialInfo')}</h1>
                    <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
                    <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
                        {t('financialGrowth.subtitle')}
                    </p>
                </div>
            </section>

            {/* 2. Financial Stats Grid */}
            <Stats showAll={true} />

            {/* 3. Financial Growth Overview */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('financialGrowth.overview.title')}</h2>
                        <div className="w-16 h-1 bg-secondary mx-auto"></div>
                        <p className="mt-4 text-gray-600 max-w-3xl mx-auto">{t('financialGrowth.overview.description')}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-primary text-2xl mb-6">
                                <MdTrendingUp />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('financialGrowth.highlights.growth')}</h3>
                            <p className="text-gray-600 leading-relaxed">{t('financialGrowth.highlights.growthDesc')}</p>
                        </div>
                        <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-primary text-2xl mb-6">
                                <MdAccountBalance />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('financialGrowth.highlights.stability')}</h3>
                            <p className="text-gray-600 leading-relaxed">{t('financialGrowth.highlights.stabilityDesc')}</p>
                        </div>
                        <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-primary text-2xl mb-6">
                                <FaBullseye />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('financialGrowth.highlights.future')}</h3>
                            <p className="text-gray-600 leading-relaxed">{t('financialGrowth.highlights.futureDesc')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. Member Services Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-2 block">{t('navbar.memberServicesBadge')}</span>
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('memberServices.services.title')}</h2>
                        <div className="w-16 h-1 bg-secondary mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service) => (
                            <div
                                key={service.id}
                                id={service.id === 4 ? "insurance" : undefined}
                                className="group p-6 bg-slate-50 rounded-xl border border-slate-100 hover:bg-white hover:shadow-xl hover:border-secondary/20 transition-all duration-300 scroll-mt-28 target:ring-4 target:ring-secondary target:ring-offset-4"
                            >
                                <div className="text-3xl text-primary mb-4">
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{t(service.titleKey)}</h3>
                                <p className="text-gray-600 text-sm mb-6 leading-relaxed">{t(service.descKey)}</p>
                                <ul className="space-y-2">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                            <FaCheckCircle className="text-secondary mt-1 flex-shrink-0" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 5. Milestones Timeline */}
            <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4 text-white">{t('financialGrowth.milestones.title')}</h2>
                        <div className="w-16 h-1 bg-secondary mx-auto"></div>
                    </div>

                    <div className="relative">
                        {/* Timeline Line */}
                        <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-white/20"></div>

                        <div className="space-y-12">
                            {milestones.map((milestone, index) => (
                                <div key={index} className="flex items-center md:flex-row">
                                    {/* Left Column (Desktop) */}
                                    <div className="flex-1 md:pr-12 hidden md:block text-right">
                                        {index % 2 === 0 && (
                                            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                                                <div className="text-secondary font-bold text-2xl mb-2">{milestone.year}</div>
                                                <h3 className="text-xl font-bold text-white mb-3">{milestone.title}</h3>
                                                <p className="text-blue-100 text-sm leading-relaxed">{milestone.desc}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Center Trophy Icon (Desktop) */}
                                    <div className="hidden md:flex w-10 h-10 bg-secondary rounded-full items-center justify-center z-10 flex-shrink-0 border-4 border-slate-900">
                                        <FaTrophy className="text-slate-900 text-sm" />
                                    </div>

                                    {/* Right Column (Desktop) */}
                                    <div className="flex-1 md:pl-12 hidden md:block text-left">
                                        {index % 2 !== 0 && (
                                            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                                                <div className="text-secondary font-bold text-2xl mb-2">{milestone.year}</div>
                                                <h3 className="text-xl font-bold text-white mb-3">{milestone.title}</h3>
                                                <p className="text-blue-100 text-sm leading-relaxed">{milestone.desc}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Mobile View */}
                                    <div className="md:hidden w-full bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="text-secondary font-bold text-xl">{milestone.year}</div>
                                            <div className="h-0.5 flex-grow bg-white/10"></div>
                                        </div>
                                        <h3 className="text-lg font-bold text-white mb-2">{milestone.title}</h3>
                                        <p className="text-blue-100 text-sm">{milestone.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. Achievements List */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl shadow-2xl p-8 md:p-12 text-white">
                        <div className="text-center mb-10">
                            <FaAward className="text-6xl text-secondary mx-auto mb-6 opacity-80" />
                            <h2 className="text-3xl font-bold mb-4">{t('financialGrowth.achievements.title')}</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {achievements.map((achievement, index) => (
                                <div key={index} className="flex items-start gap-4 p-4 bg-white/10 rounded-lg border border-white/10 hover:bg-white/20 transition-colors">
                                    <div className="flex-shrink-0 w-6 h-6 bg-secondary text-primary rounded-full flex items-center justify-center font-bold text-xs mt-1">
                                        ✓
                                    </div>
                                    <span className="text-blue-50 text-base leading-relaxed">{achievement}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default FinancialInfo;
